import type { CollectionRecord, RecipeMemberships } from './records.js';
import type { CollectionId, RecipeId } from './types.js';
import { MAIN_COLLECTION_ID } from './types.js';

export interface RepairPlan {
  /** Recipes that ended up in zero collections and must be adopted by Main. */
  readonly adoptIntoMain: readonly RecipeId[];
  /** Membership entries pointing at a collection that no longer exists. */
  readonly dropMemberships: readonly { recipeId: RecipeId; collectionId: CollectionId }[];
}

export const EMPTY_REPAIR_PLAN: RepairPlan = { adoptIntoMain: [], dropMemberships: [] };

export function isRepairNeeded(plan: RepairPlan): boolean {
  return plan.adoptIntoMain.length > 0 || plan.dropMemberships.length > 0;
}

/**
 * "Every recipe is in at least one collection" is a cross-field invariant, and a CRDT
 * cannot enforce it at write time: two devices can each remove the recipe from one of its
 * last two collections, and both edits are individually valid. So it is repaired instead.
 *
 * Every rule here is a pure function of document state, which is what makes it safe to run
 * on every replica independently — each one computes the same plan and converges without
 * coordinating.
 *
 * Note that a *soft-deleted* collection still counts as membership. During the 30-day
 * restore window nothing moves, so restoring a collection is a clean rewind rather than a
 * scramble to work out where its recipes went.
 */
export function planRepairs(
  recipes: readonly RecipeMemberships[],
  collections: readonly CollectionRecord[],
): RepairPlan {
  const existing = new Set(collections.map((c) => c.id));
  const adoptIntoMain: RecipeId[] = [];
  const dropMemberships: { recipeId: RecipeId; collectionId: CollectionId }[] = [];

  for (const recipe of recipes) {
    let remaining = 0;
    for (const collectionId of Object.keys(recipe.collections)) {
      if (existing.has(collectionId)) {
        remaining += 1;
      } else {
        dropMemberships.push({ recipeId: recipe.id, collectionId });
      }
    }
    if (remaining === 0) adoptIntoMain.push(recipe.id);
  }

  // Sorted so two replicas producing the same plan also produce it in the same order,
  // which keeps the resulting update bytes identical.
  adoptIntoMain.sort();
  dropMemberships.sort((a, b) =>
    a.recipeId === b.recipeId
      ? a.collectionId.localeCompare(b.collectionId)
      : a.recipeId.localeCompare(b.recipeId),
  );

  return { adoptIntoMain, dropMemberships };
}

export function mainCollectionRecord(): CollectionRecord {
  // Fully deterministic: two replicas seeding a fresh document produce byte-identical
  // entries, so the system collection can never end up duplicated.
  return {
    id: MAIN_COLLECTION_ID,
    name: 'Main',
    description: 'Your default collection. New recipes are saved here.',
    icon: 'inbox',
    order: 'a0',
    isSystem: true,
    // Deterministic zeros so two replicas seeding a fresh document converge.
    createdAt: 0,
    updatedAt: 0,
    deletedAt: null,
  };
}
